<template>
  <modal-wrap :name="modalName" size="xl" :hide-footer="false">
    <template #modal-title> Edit customer general info </template>
    <template #default>
      <div class="edit-customer-data">
        <VeeForm v-if="userData" @submit="onSubmit">
          <b-form-group class="form-group">
            <div class="form-inline-row">
              <label class="label-input">Email address</label>
              <VInput
                v-model="customer.email"
                :rules="{ required: true, email: true }"
                autocomplete="off"
                type="email"
                name="email"
                placeholder="Email address"
              />
            </div>

            <div class="form-inline-row">
              <label class="label-input">Client №</label>
              <VInput
                v-model="customer.clientNumber"
                :rules="{ required: true }"
                type="text"
                name="clientNumber"
                placeholder="Client №"
              />
            </div>

            <div class="form-inline-row">
              <label class="label-input">Contact First name</label>
              <VInput
                v-model="customer.contactName"
                :rules="{ required: true }"
                type="text"
                name="contactName"
                placeholder="First name"
              />
            </div>

            <div class="form-inline-row">
              <label class="label-input">Contact Last name</label>
              <VInput
                v-model="customer.contactLastName"
                :rules="{ required: true }"
                type="text"
                name="contactLastName"
                placeholder="Last name"
              />
            </div>

            <div class="form-inline-row">
              <label class="label-input">Company</label>
              <VInput v-model="customer.name" type="text" name="name" placeholder="Company" />
            </div>

            <div class="form-inline-row">
              <label class="label-input">Phone number</label>
              <VInput
                v-model="customer.phoneNumber"
                :rules="{ required: true }"
                type="text"
                name="phoneNumber"
                placeholder="Phone number"
              />
            </div>

            <div class="form-inline-row">
              <label class="label-input">
                <span class="d-inline-flex align-items-center">
                  <span>Automatic parcel pickup</span>
                  <AutoPickupInfoModal class="ml-1" />
                </span>
              </label>
              <select
                v-model="customer.autoPickupForParcelShipments"
                class="form-control"
                name="autoPickupForParcelShipments"
              >
                <option :value="true">Yes</option>
                <option :value="false">No</option>
              </select>
            </div>

            <div class="form-inline-row">
              <label class="label-input">
                <span class="d-inline-flex align-items-center">
                  <span>Inventory management</span>
                  <InventoryManagementInfoModal class="ml-1" />
                </span>
              </label>
              <select
                v-model="customer.isInventoryManagementAvailable"
                class="form-control"
                name="isInventoryManagementAvailable"
              >
                <option :value="true">Yes</option>
                <option :value="false">No</option>
              </select>
            </div>

            <div class="form-inline-row">
              <label class="label-input">Address book</label>
              <select
                v-model="customer.isAddressBookAvailable"
                class="form-control"
                name="isAddressBookAvailable"
              >
                <option :value="true">Yes</option>
                <option :value="false">No</option>
              </select>
            </div>

            <div class="form-inline-row">
              <label class="label-input">Short name</label>
              <VInput v-model="customer.shortName" type="text" name="shortName" placeholder="Short name" />
            </div>

            <div class="form-inline-row">
              <label class="label-input">Contract party</label>
              <VInput v-model="customer.contractParty" type="text" name="contractParty" placeholder="Contract party" />
            </div>

            <div class="form-inline-row">
              <label class="label-input">External ID (optional)</label>
              <VInput v-model="customer.externalId" type="text" name="externalId" placeholder="External ID" />
            </div>

            <div class="form-inline-row">
              <label class="label-input">Assigned Salesman</label>
              <v-select
                v-model="customer.salesmanId"
                :options="staffsOptions"
                fieldset-class="form-group position-relative"
                name="assignedSalesman"
                placeholder="Assigned Salesman"
              />
            </div>

            <div class="form-inline-row mb-0">
              <label class="label-input">Custom Options</label>

              <div class="form-group position-relative p-top">
                <div class="form-check">
                  <input
                    id="hidePriceCheckbox"
                    v-model="customer.hidePrice"
                    class="form-check-input"
                    type="checkbox"
                    :value="true"
                  />
                  <label class="form-check-label" for="hidePriceCheckbox">
                    Uncheck this if you want to show optimized prices for the customer
                  </label>
                </div>
              </div>
            </div>

            <div class="form-inline-row">
              <label class="label-input"></label>

              <div class="form-group position-relative p-top">
                <div class="form-check">
                  <input
                    id="shouldDisplayScansCheckbox"
                    v-model="customer.shouldDisplayScans"
                    class="form-check-input"
                    type="checkbox"
                    :value="true"
                  />
                  <label class="form-check-label" for="shouldDisplayScansCheckbox">
                    Check if you want to show internal scans for all
                  </label>
                </div>
              </div>
            </div>
          </b-form-group>
        </VeeForm>
      </div>
    </template>
    <template #modal-footer>
      <div>
        <b-button size="md" class="btn-grey mr-3" @click="cancelEdit"> Cancel </b-button>
        <b-button size="md" class="btn-green" :disabled="!isFormChanged" type="submit" @click="onSubmit">
          Save changes
        </b-button>
      </div>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { SelectOptions } from '@/types/Components/SelectOptions'
import type { Company } from '@/types/Models/Company'
import type { User } from '@/types/Models/User'
import { notify } from '@kyvg/vue3-notification'
import { BButton, BFormGroup } from 'bootstrap-vue-next'
import { Form as VeeForm } from 'vee-validate'
import { ref, computed, defineComponent, toRaw, onMounted, watch, type ComputedRef, type Ref } from 'vue'
import { EDIT_COMPANY_GENERAL_INFO_MODAL } from '@/const/ModalTypes'
import { none } from '@/services/Helpers'
import { isEqual } from '@/services/Utils'
import { useCustomersStore } from '@/store/customers'
import { usePackingSlipStore } from '@/store/packingSlip'
import { useVueNextModalStore } from '@/store/vueNextModal'
import VInput from '@/views/Components/Elements/Form/VInput.vue'
import VSelect from '@/views/Components/Elements/Form/VSelect.vue'
import ModalWrap from '@/views/Modals/ModalWrap.vue'
import AutoPickupInfoModal from '@/views/Components/Customer/AutoPickupInfoModal.vue'
import InventoryManagementInfoModal from '@/views/Components/Customer/InventoryManagementInfoModal.vue'

export default defineComponent({
  name: 'EditCompanyGeneralInfoModal',
  components: { BFormGroup, BButton, ModalWrap, VeeForm, VInput, VSelect, AutoPickupInfoModal, InventoryManagementInfoModal },
  emits: ['update'],
  setup(_, { emit }) {
    const customerStore = useCustomersStore()
    const packingSlipStore = usePackingSlipStore()
    const modalStore = useVueNextModalStore()
    const modalName: string = EDIT_COMPANY_GENERAL_INFO_MODAL
    const userData: ComputedRef<Company> = computed(() => modalStore.modalPropsByName(modalName))
    const customer: Ref<Partial<Company>> = ref({
      email: '',
      clientNumber: '',
      contactName: '',
      contactLastName: '',
      name: '',
      phoneNumber: '',
      contractParty: '',
      shortName: '',
      salesmanId: '',
      externalId: '',
      autoPickupForParcelShipments: false,
      isInventoryManagementAvailable: false,
      isAddressBookAvailable: false,
      shouldDisplayScans: false,
      hidePrice: false,
    })

    const staffsOptions: ComputedRef<SelectOptions[]> = computed(() => {
      const result: SelectOptions[] = [
        {
          value: '',
          text: 'Choose Assigned Salesman',
        },
      ]
      if (!packingSlipStore.staffs?.length) {
        return result
      }

      return result.concat(
        packingSlipStore.staffs.map((staff: User) => ({
          value: staff.id ?? null,
          text: staff.firstName ? `${staff.firstName} ${staff.lastName}` : staff.email,
        })),
      )
    })

    const isFormChanged = computed(() => {
      const customerData = { ...customer.value }
      return !isEqual(customerData, userData.value)
    })

    const onSubmit = () => {
      let data: any = { ...toRaw(customer.value) }
      // delete data.assignedSalesman
      console.log(data)
      customerStore
        .update({ id: userData.value.id, data })
        .then(() => {
          notify({ group: 'success', text: 'Customer data updated' })
          emit('update', data)
          modalStore.closeModal(modalName)
        })
        .catch(none)
    }

    const cancelEdit = () => {
      modalStore.closeModal(modalName)
    }

    const loadAddressData = () => {
      if (userData.value) {
        customer.value = {
          email: userData.value.email,
          clientNumber: userData.value.clientNumber,
          contactName: userData.value.contactName,
          contactLastName: userData.value.contactLastName,
          name: userData.value.name,
          phoneNumber: userData.value.phoneNumber,
          contractParty: userData.value.contractParty,
          shortName: userData.value.shortName,
          salesmanId: userData.value.salesmanId,
          externalId: userData.value.externalId,
          autoPickupForParcelShipments: userData.value.autoPickupForParcelShipments ?? false,
          isInventoryManagementAvailable: userData.value.isInventoryManagementAvailable ?? false,
          isAddressBookAvailable: userData.value.isAddressBookAvailable,
          shouldDisplayScans: userData.value.shouldDisplayScans || false,
          hidePrice: userData.value.hidePrice || false,
        }
      }
    }

    onMounted(loadAddressData)

    watch(userData, loadAddressData)

    return {
      customer,
      modalName,
      userData,
      staffsOptions,
      isFormChanged,
      onSubmit,
      cancelEdit,
    }
  },
})
</script>
